import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    forwardRef,
    ForwardedRef
} from 'react';
import {
    Room,
    RoomEvent,
    Participant,
    RemoteParticipant,
    RemoteTrack,
    Track,
    LocalTrack,
    LocalVideoTrack,
    LocalAudioTrack,
    createLocalAudioTrack,
    createLocalVideoTrack,
    VideoPreset,
    VideoPresets,
    TrackPublication,
    LocalParticipant
} from 'livekit-client';
import {
    Box,
    Button,
    Typography,
    Grid,
    Alert,
    IconButton,
    CircularProgress,
    Stack,
    SxProps,
    Theme
} from '@mui/material';
import {
    Mic as MicIcon,
    MicOff as MicOffIcon,
    ScreenShare as ScreenShareIcon,
    StopScreenShare as StopScreenShareIcon,
    Videocam as VideoIcon,
    VideocamOff as VideoOffIcon,
    CallEnd as CallEndIcon,
    Settings as SettingsIcon,
    VolumeUp as VolumeUpIcon,
    VolumeOff as VolumeOffIcon,
    Fullscreen as FullscreenIcon,
    FullscreenExit as FullscreenExitIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { Log } from '@fanfanlo';
import { mainModel } from '@src/libs/mc/main/MainModel';
import { MeetingRoomProps, VideoPresetConfig, ExtendedParticipantState, ParticipantState } from './meeting-room.intf';

// 日志记录器
const logger = new Log(false, 'livekit-meeting.MeetingRoom');
const uiLogger = logger.sub(false, 'MeetingRoom_ui');

// 视频预设
const VIDEO_PRESET: VideoPresetConfig = {
    width: 1280,
    height: 720,
    // @ts-ignore - frameRate is used by LiveKit internally
    frameRate: 24,
    maxBitrate: 3000 * 1000, // 3Mbps
};

// 类型定义已移至 meetint-room.intf.ts


function getTokenEndpoint(): string {
    if (!mainModel.appConfig?.livekit?.jwt) {
        throw new Error('LiveKit JWT endpoint is not configured');
    }
    return `${mainModel.appConfig.livekit.jwt}/token`;
}
// 主组件实现
function MeetingRoomComponent(
    {
        roomName,
        userName,
        onDisconnect,
        onError,
        className = '',
        style,
        sx
    }: MeetingRoomProps,
    ref: ForwardedRef<HTMLDivElement>
) {
    // 全局音视频控制状态
    const [globalMediaState, setGlobalMediaState] = useState<{
        isAudioEnabled: boolean;
        isVideoEnabled: boolean;
    }>({
        isAudioEnabled: true,
        isVideoEnabled: true,
    });

    // Refs
    const roomRef = useRef<Room | null>(null);
    const localTracksRef = useRef<LocalTrack[]>([]);
    const videoElements = useRef<Record<string, HTMLVideoElement>>({});
    const audioElements = useRef<Record<string, HTMLAudioElement>>({});
    const [participantStates, setParticipantStates] = useState<Record<string, ExtendedParticipantState>>({});
    const isUnmountedRef = useRef(false);

    // State
    const [isConnecting, setIsConnecting] = useState(false);
    const [hasConnected, setHasConnected] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isMuted, setIsMuted] = useState(false);
    const [isVideoEnabled, setIsVideoEnabled] = useState(true);
    const [participants, setParticipants] = useState<RemoteParticipant[]>([]);

    // 获取 token
    const fetchToken = useCallback(async (roomName: string, identity: string) => {
        try {
            const response = await fetch(getTokenEndpoint(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    room_name: roomName,
                    identity,
                }),
            });

            if (!response.ok) {
                throw new Error('获取 token 失败');
            }

            const data = await response.json();
            return data.token;
        } catch (error) {
            uiLogger.error('获取 token 失败:', error);
            throw error;
        }
    }, []);

    // 连接到房间
    const connectToRoom = useCallback(async () => {
        if (isConnecting || hasConnected) return;

        setIsConnecting(true);
        setError(null);

        try {
            // 1. 获取 token
            const token = await fetchToken(roomName, userName);

            // 2. 创建本地轨道
            const { audioTrack, videoTrack } = await createLocalTracks();
            localTracksRef.current = [audioTrack, videoTrack];

            // 3. 创建房间连接
            const room = new Room({
                adaptiveStream: true,
                dynacast: true,
                videoCaptureDefaults: {
                    resolution: VIDEO_PRESET,
                },
                // @ts-ignore - publishDefaults is a valid option
                publishDefaults: {
                    dtx: true,
                    red: true,
                    forceStereo: false,
                    simulcast: true,
                } as any, // Type assertion to bypass type checking for now
            });

            // 4. 设置事件监听
            setupRoomListeners(room);

            // 5. 连接到房间
            await room.connect(
                mainModel.appConfig!.livekit.wsUrl,
                token,
                {
                    autoSubscribe: true,
                }
            );

            // 6. 发布本地轨道
            try {
                await Promise.all([
                    room.localParticipant.publishTrack(audioTrack),
                    room.localParticipant.publishTrack(videoTrack)
                ]);
            } catch (publishError) {
                uiLogger.error('发布轨道失败:', publishError);
                // 即使发布失败也继续，因为可能已经有其他参与者发布了相同的轨道
            }

            // 7. 更新引用和状态
            roomRef.current = room;
            setHasConnected(true);

            // 8. 添加现有远程参与者到状态
            const remoteParticipants = Array.from(room.remoteParticipants.values());
            setParticipants(remoteParticipants);

            // 9. 更新本地参与者状态
            updateParticipantState(room.localParticipant.identity, {
                isAudioEnabled: true,
                isVideoEnabled: true,
                audioTrack,
                videoTrack,
            });

            // 10. 为现有远程参与者设置轨道
            remoteParticipants.forEach(participant => {
                // 初始化远程参与者状态
                initParticipantState(participant);
                uiLogger.log('已初始化远程参与者状态:', participant.identity);
                
                // 使用 getTrackPublications 获取所有轨道发布
                participant.getTrackPublications().forEach(publication => {
                    if (publication.track && publication.track instanceof RemoteTrack) {
                        handleTrackSubscribed(publication.track, publication, participant);
                    }
                });
            });
            
            // 初始化本地参与者状态
            if (room.localParticipant) {
                initParticipantState(room.localParticipant);
                uiLogger.log('已初始化本地参与者状态:', room.localParticipant.identity);
            }

            uiLogger.log('成功加入会议');

        } catch (error) {
            uiLogger.error('连接房间失败:', error);
            setError('加入会议失败，请检查网络和权限设置');
            // 发生错误时清理资源
            cleanupTracks(localTracksRef.current);
            localTracksRef.current = [];

            if (roomRef.current) {
                try {
                    await roomRef.current.disconnect();
                } catch (e) {
                    uiLogger.error('断开连接时出错:', e);
                }
                roomRef.current = null;
            }
        } finally {
            setIsConnecting(false);
        }
    }, [roomName, userName, isConnecting, hasConnected, fetchToken]);

    // 创建本地音视频轨道
    const createLocalTracks = useCallback(async () => {
        try {
            const [audioTrack, videoTrack] = await Promise.all([
                createLocalAudioTrack({
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                }),
                createLocalVideoTrack({
                    resolution: {
                        width: 1280,
                        height: 720,
                        frameRate: 24,
                    },
                }),
            ]);
            return { audioTrack, videoTrack };
        } catch (error) {
            uiLogger.error('创建本地轨道失败:', error);
            throw error;
        }
    }, []);

    // 清理轨道
    const cleanupTracks = useCallback((tracks: LocalTrack[]) => {
        tracks.forEach(track => {
            track.stop();
            track.detach();
        });
    }, []);

    // 跟踪已处理的轨道元素
    const trackElementsRef = useRef<Record<string, boolean>>({});
    const videoElementsRef = useRef<Record<string, HTMLVideoElement>>({});
    const audioElementsRef = useRef<Record<string, HTMLAudioElement>>({});

    // 更新参与者状态
    const updateParticipantState = useCallback((identity: string, state: Partial<ParticipantState>) => {
        setParticipantStates(prev => ({
            ...prev,
            [identity]: {
                ...(prev[identity] || {
                    isAudioEnabled: true,
                    isVideoEnabled: true,
                    isAudioEnabledGlobal: true,
                    isVideoEnabledGlobal: true,
                }),
                ...state,
            },
        }));
    }, []);
    // 处理轨道订阅
    const handleTrackSubscribed = useCallback((
        track: RemoteTrack,
        publication: TrackPublication,
        participant: Participant
    ) => {
        if (!track) return () => { };

        const { identity } = participant;
        const isVideoTrack = track.kind === Track.Kind.Video;

        uiLogger.log(`处理 ${track.kind} 轨道订阅:`, identity, track);

        // 更新轨道状态的函数
        const updateTrackState = () => {
            const isMuted = track.isMuted;
            const stateUpdate: Partial<ParticipantState> = {};

            if (track.kind === Track.Kind.Audio) {
                stateUpdate.isAudioEnabled = !isMuted;
            } else if (track.kind === Track.Kind.Video) {
                stateUpdate.isVideoEnabled = !isMuted;
            }

            updateParticipantState(identity, stateUpdate);

            // 强制触发重新渲染以确保 ParticipantVideo 组件更新
            if (isVideoTrack) {
                setParticipants(prev => [...prev]);
            }
        };

        // 监听轨道静音/取消静音事件
        track.on('muted', updateTrackState);
        track.on('unmuted', updateTrackState);

        // 初始状态更新
        updateTrackState();

        // 强制更新参与者列表以触发重新渲染
        // 这确保了即使没有状态变化，组件也会检查新的轨道
        setParticipants(prev => {
            const participantExists = prev.some(p => p.identity === identity);
            if (!participantExists) {
                return [...prev, participant as RemoteParticipant];
            }
            return [...prev];
        });


        // 清理函数
        return () => {
            track.off('muted', updateTrackState);
            track.off('unmuted', updateTrackState);
        };
    }, [updateParticipantState]);

    // 设置房间事件监听
    const setupRoomListeners = useCallback((room: Room) => {
        // 保存所有事件处理函数引用，以便后续清理
        type EventHandler = (...args: any[]) => void;
        const eventHandlers: Record<string, EventHandler> = {
            // 处理远程参与者连接
            participantConnected: (participant: RemoteParticipant) => {
                if (!(participant instanceof RemoteParticipant)) return;

                uiLogger.log('远程参与者已连接:', participant.identity);

                // 添加新参与者到状态（使用函数式更新确保不重复添加）
                setParticipants(prev => {
                    // 如果已经存在该参与者，则返回原数组
                    if (prev.some(p => p.identity === participant.identity)) {
                        return prev;
                    }
                    return [...prev, participant];
                });

                // 监听参与者的轨道发布
                const handleTrackPublished = (publication: TrackPublication) => {
                    uiLogger.log('远程参与者发布轨道:', publication.kind, participant.identity);
                    if (publication.track) {
                        handleTrackSubscribed(publication.track as RemoteTrack, publication, participant);
                    } else {
                        publication.once('subscribed', (track: RemoteTrack) => {
                            handleTrackSubscribed(track, publication, participant);
                        });
                    }
                };

                // 添加轨道发布监听
                participant.on('trackPublished', handleTrackPublished);

                // 处理已经发布的轨道
                participant.trackPublications.forEach(publication => {
                    if (publication.track) {
                        handleTrackSubscribed(publication.track as RemoteTrack, publication, participant);
                    }
                });

                // 返回清理函数
                return () => {
                    participant.off('trackPublished', handleTrackPublished);
                };
            },

            // 处理参与者断开连接
            participantDisconnected: (participant: RemoteParticipant) => {
                uiLogger.log('远程参与者已断开连接:', participant.identity);
                setParticipants(prev => {
                    const newParticipants = prev.filter(p => p.identity !== participant.identity);
                    uiLogger.log('更新后的参与者列表:', newParticipants.map(p => p.identity));
                    return newParticipants;
                });
            },

            // 处理轨道订阅
            trackSubscribed: (track: RemoteTrack, publication: TrackPublication, participant: Participant) => {
                if (!track) return;
                uiLogger.log('轨道订阅成功:', track.kind, participant.identity);
                // 处理远程轨道订阅
                handleTrackSubscribed(track, publication, participant as RemoteParticipant);
            },

            disconnected: (reason?: any) => {
                const reasonStr = reason?.toString?.() || '未知原因';
                uiLogger.log('已从房间断开连接，原因:', reasonStr);
                // 更新状态但不调用 handleDisconnect，因为可能是我们主动断开的
                setHasConnected(false);
                setParticipants([]);
                setError(null);
            },

            reconnecting: () => {
                uiLogger.log('正在重新连接到房间...');
                setError('正在重新连接...');
            },

            reconnected: () => {
                uiLogger.log('已重新连接到房间');
                setError(null);
            }
        };

        // 添加事件监听
        room
            .on(RoomEvent.Disconnected, eventHandlers.disconnected)
            .on(RoomEvent.Reconnecting, eventHandlers.reconnecting)
            .on(RoomEvent.Reconnected, eventHandlers.reconnected)
            .on(RoomEvent.ParticipantConnected, eventHandlers.participantConnected)
            .on(RoomEvent.ParticipantDisconnected, eventHandlers.participantDisconnected)
            .on(RoomEvent.TrackSubscribed, eventHandlers.trackSubscribed);

        // 返回清理函数
        return () => {
            if (!room) return;
            room
                .off(RoomEvent.Disconnected, eventHandlers.disconnected)
                .off(RoomEvent.Reconnecting, eventHandlers.reconnecting)
                .off(RoomEvent.Reconnected, eventHandlers.reconnected)
                .off(RoomEvent.ParticipantConnected, eventHandlers.participantConnected)
                .off(RoomEvent.ParticipantDisconnected, eventHandlers.participantDisconnected)
                .off(RoomEvent.TrackSubscribed, eventHandlers.trackSubscribed);

            // 确保返回 void
            return undefined;
        };
    }, [handleTrackSubscribed]);

    // 离开会议
    const leaveMeeting = useCallback(async () => {
        uiLogger.log('正在离开会议...');
        try {
            // 停止所有本地轨道
            cleanupTracks(localTracksRef.current);

            // 断开房间连接
            if (roomRef.current) {
                await roomRef.current.disconnect();
                roomRef.current = null;
            }

            // 更新状态
            setHasConnected(false);
            setParticipants([]);
            setError(null);

            // 清除所有媒体元素
            Object.values(videoElements.current).forEach(el => el && el.remove());
            Object.values(audioElements.current).forEach(el => el && el.remove());
            videoElements.current = {};
            audioElements.current = {};

            // 调用断开连接回调
            if (onDisconnect) {
                onDisconnect();
            }

            uiLogger.log('已成功离开会议');
        } catch (error) {
            uiLogger.error('离开会议时出错:', error);
            setError('离开会议时出错，请重试');
        }
    }, [onDisconnect]);

    // 清理函数
    const cleanup = useCallback(async (isUnmounting = false) => {
        // 如果是组件卸载，设置卸载标志
        if (isUnmounting) {
            isUnmountedRef.current = true;
        }

        uiLogger.log('执行清理...');
        if (!roomRef.current) return;

        const localParticipant = roomRef.current.localParticipant;
        const audioTrack = localTracksRef.current.find(
            t => t.kind === Track.Kind.Audio
        ) as LocalAudioTrack | undefined;
        const videoTrack = localTracksRef.current.find(
            t => t.kind === Track.Kind.Video
        ) as LocalVideoTrack | undefined;

        try {
            if (audioTrack) {
                await audioTrack.stop();
            }
            if (videoTrack) {
                await videoTrack.stop();
            }
        } catch (error) {
            uiLogger.error('停止轨道失败:', error);
        }

        // 清理轨道
        cleanupTracks(localTracksRef.current);
        localTracksRef.current = [];

        // 清理房间
        if (roomRef.current) {
            roomRef.current.disconnect();
            roomRef.current = null;
        }

        // 清理状态
        setHasConnected(false);
        setParticipants([]);
        setParticipantStates({});
    }, []);

    // 更新全局媒体状态
    const updateGlobalMediaState = useCallback((audioEnabled: boolean, videoEnabled: boolean) => {
        setGlobalMediaState(prev => ({
            ...prev,
            isAudioEnabled: audioEnabled,
            isVideoEnabled: videoEnabled,
        }));
    }, []);

    // 切换静音状态（全局控制）
    const toggleMute = useCallback(async () => {
        if (!roomRef.current) return;

        const localParticipant = roomRef.current.localParticipant;
        const audioTrack = localTracksRef.current.find(
            t => t.kind === Track.Kind.Audio
        ) as LocalAudioTrack | undefined;

        if (!audioTrack) return;

        try {
            // 全局按钮基于全局状态进行切换
            const newGlobalAudioState = !globalMediaState.isAudioEnabled;

            // 同步更新本地麦克风的物理状态
            if (newGlobalAudioState) {
                await audioTrack.unmute();
            } else {
                await audioTrack.mute();
            }

            // 更新本地UI状态
            setIsMuted(!newGlobalAudioState);
            updateParticipantState(localParticipant.identity, { isAudioEnabled: newGlobalAudioState });

            // 更新全局音频状态，这将影响所有参与者的有效状态
            updateGlobalMediaState(newGlobalAudioState, globalMediaState.isVideoEnabled);
        } catch (error) {
            uiLogger.error('切换静音状态失败:', error);
            setError('切换麦克风状态失败');
        }
    }, [globalMediaState, updateParticipantState, updateGlobalMediaState]);

    // 切换视频状态（全局控制）
    const toggleVideo = useCallback(async () => {
        if (!roomRef.current) return;

        const localParticipant = roomRef.current.localParticipant;
        const videoTrack = localTracksRef.current.find(
            t => t.kind === Track.Kind.Video
        ) as LocalVideoTrack | undefined;

        if (!videoTrack) return;

        try {
            // 全局按钮基于全局状态进行切换
            const newGlobalVideoState = !globalMediaState.isVideoEnabled;

            // 同步更新本地摄像头的物理状态
            if (newGlobalVideoState) {
                await videoTrack.unmute();
            } else {
                await videoTrack.mute();
            }

            // 更新本地UI状态
            setIsVideoEnabled(newGlobalVideoState);
            updateParticipantState(localParticipant.identity, { isVideoEnabled: newGlobalVideoState });

            // 更新全局视频状态，这将影响所有参与者的有效状态
            updateGlobalMediaState(globalMediaState.isAudioEnabled, newGlobalVideoState);
        } catch (error) {
            uiLogger.error('切换视频状态失败:', error);
            setError('切换摄像头状态失败');
        }
    }, [globalMediaState, updateParticipantState, updateGlobalMediaState]);

    // 切换本地静音状态（仅本地tile）
    const handleLocalMuteToggle = useCallback(async () => {
        if (!roomRef.current) return;

        const localParticipant = roomRef.current.localParticipant;
        const audioTrack = localTracksRef.current.find(
            t => t.kind === Track.Kind.Audio
        ) as LocalAudioTrack | undefined;

        if (!audioTrack) return;

        try {
            const newMutedState = !isMuted;
            if (newMutedState) {
                await audioTrack.mute();
            } else {
                await audioTrack.unmute();
            }

            setIsMuted(newMutedState);
            updateParticipantState(localParticipant.identity, { isAudioEnabled: !newMutedState });
        } catch (error) {
            uiLogger.error('切换本地静音状态失败:', error);
            setError('切换麦克风状态失败');
        }
    }, [isMuted, updateParticipantState]);

    // 切换本地视频状态（仅本地tile）
    const handleLocalVideoToggle = useCallback(async () => {
        if (!roomRef.current) return;

        const localParticipant = roomRef.current.localParticipant;
        const videoTrack = localTracksRef.current.find(
            t => t.kind === Track.Kind.Video
        ) as LocalVideoTrack | undefined;

        if (!videoTrack) return;

        try {
            const newVideoState = !isVideoEnabled;
            if (newVideoState) {
                await videoTrack.unmute();
            } else {
                await videoTrack.mute();
            }

            setIsVideoEnabled(newVideoState);
            updateParticipantState(localParticipant.identity, { isVideoEnabled: newVideoState });
        } catch (error) {
            uiLogger.error('切换本地视频状态失败:', error);
            setError('切换摄像头状态失败');
        }
    }, [isVideoEnabled, updateParticipantState]);

    // 组件卸载时清理资源
    useEffect(() => {
        return () => {
            // 清理资源
            cleanup(true);
        };
    }, []);

    // 获取参与者状态
    const getParticipantState = useCallback((identity: string): ParticipantState => {
        return participantStates[identity] || {
            isAudioEnabled: true,
            isVideoEnabled: true,
            isAudioEnabledGlobal: true,
            isVideoEnabledGlobal: true,
        };
    }, [participantStates]);

    // 初始化参与者状态
    const initParticipantState = useCallback((participant: Participant) => {
        const state = getParticipantState(participant.identity);

        // 更新音频状态
        const audioTrack = participant.getTrackPublications().find(
            pub => pub.kind === Track.Kind.Audio
        )?.track as LocalAudioTrack | undefined;

        // 更新视频状态
        const videoTrack = participant.getTrackPublications().find(
            pub => pub.kind === Track.Kind.Video
        )?.track as LocalVideoTrack | undefined;

        updateParticipantState(participant.identity, {
            ...state,
            isAudioEnabled: audioTrack ? !audioTrack.isMuted : false,
            isVideoEnabled: videoTrack ? !videoTrack.isMuted : false
        });
    }, [getParticipantState, updateParticipantState]);

    // 切换参与者的音频状态
    const handleToggleAudio = useCallback((participant: Participant) => {
        const currentState = participantStates[participant.identity] || {
            isAudioEnabled: true,
            isVideoEnabled: true,
            isAudioEnabledGlobal: true,
            isVideoEnabledGlobal: true,
        };

        updateParticipantState(participant.identity, {
            ...currentState,
            isAudioEnabled: !currentState.isAudioEnabled
        });
    }, [participantStates, updateParticipantState]);

    // 切换参与者的视频状态
    const handleToggleVideo = useCallback((participant: Participant) => {
        const currentState = participantStates[participant.identity] || {
            isAudioEnabled: true,
            isVideoEnabled: true,
            isAudioEnabledGlobal: true,
            isVideoEnabledGlobal: true,
        };

        updateParticipantState(participant.identity, {
            ...currentState,
            isVideoEnabled: !currentState.isVideoEnabled
        });
    }, [participantStates, updateParticipantState]);

    // 获取参与者的实际媒体状态（考虑全局覆盖）
    const getEffectiveMediaState = useCallback((participantId: string) => {
        const state = participantStates[participantId];
        if (!state) return { isAudioEnabled: true, isVideoEnabled: true };

        return {
            isAudioEnabled: globalMediaState.isAudioEnabled ? state.isAudioEnabled : false,
            isVideoEnabled: globalMediaState.isVideoEnabled ? state.isVideoEnabled : false,
        };
    }, [globalMediaState, participantStates]);

    // 参与者视频组件
    const ParticipantVideo = React.memo(({
        participant,
        isLocal,
        isVideoEnabled
    }: {
        participant: Participant;
        isLocal: boolean;
        isVideoEnabled: boolean;
    }) => {
        const videoRef = useRef<HTMLVideoElement>(null);
        const [videoTrack, setVideoTrack] = useState<RemoteTrack | LocalVideoTrack | null>(null);

        // 处理视频轨道变化
        useEffect(() => {
            if (!participant) return;

            // 查找现有的视频轨道发布
            const videoPublication = Array.from(participant.trackPublications.values())
                .find(pub => pub.kind === Track.Kind.Video);

            // 如果轨道已存在，则直接设置
            if (videoPublication && videoPublication.track) {
                setVideoTrack(videoPublication.track);
            }

            // 当订阅轨道时处理
            const handleTrackSubscribed = (track: Track) => {
                if (track.kind === Track.Kind.Video) {
                    setVideoTrack(track as RemoteTrack | LocalVideoTrack);
                }
            };

            // 当取消订阅轨道时处理
            const handleTrackUnsubscribed = (track: Track) => {
                if (track.kind === Track.Kind.Video) {
                    setVideoTrack(null);
                }
            };

            // 添加事件监听
            participant.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
            participant.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);

            // 清理函数
            return () => {
                participant.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
                participant.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
            };
        }, [participant]);

        // 处理视频元素附加/分离
        useEffect(() => {
            const videoElement = videoRef.current;
            if (!videoElement || !videoTrack) return;

            // 清理现有视频源
            while (videoElement.firstChild) {
                videoElement.removeChild(videoElement.firstChild);
            }

            // 附加视频轨道
            if (isVideoEnabled) {
                videoTrack.attach(videoElement);
            }

            return () => {
                if (videoTrack) {
                    videoTrack.detach(videoElement);
                }
            };
        }, [videoTrack, isVideoEnabled]);

        return (
            <Box
                sx={{
                    width: '100%',
                    aspectRatio: '16/9',
                    bgcolor: 'grey.800',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                }}
            >
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted={isLocal}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: isVideoEnabled ? 'block' : 'none'
                    }}
                />
                {!isVideoEnabled && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'grey.900',
                        }}
                    >
                        <Box
                            sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'primary.main',
                                color: 'white',
                                fontSize: 32,
                                fontWeight: 'bold',
                            }}
                        >
                            {participant.identity.charAt(0).toUpperCase()}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    });

    // 参与者音频组件
    const ParticipantAudio = React.memo(({
        participant,
        isMuted,
    }: {
        participant: Participant;
        isMuted: boolean;
    }) => {
        const audioRef = useRef<HTMLAudioElement>(null);
        const [audioTrack, setAudioTrack] = useState<RemoteTrack | LocalAudioTrack | null>(null);

        useEffect(() => {
            if (!participant) return;

            const handleTrackSubscribed = (track: Track) => {
                if (track.kind === Track.Kind.Audio) {
                    setAudioTrack(track as RemoteTrack | LocalAudioTrack);
                }
            };

            const handleTrackUnsubscribed = (track: Track) => {
                if (track.kind === Track.Kind.Audio) {
                    setAudioTrack(null);
                }
            };

            // 查找现有的音频轨道
            const audioPublication = Array.from(participant.trackPublications.values())
                .find(pub => pub.kind === Track.Kind.Audio);

            if (audioPublication && audioPublication.track) {
                setAudioTrack(audioPublication.track);
            }

            participant.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);
            participant.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);

            return () => {
                participant.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
                participant.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed);
            };
        }, [participant]);

        useEffect(() => {
            const audioElement = audioRef.current;
            if (audioElement && audioTrack) {
                audioTrack.attach(audioElement);
                // 远程音频默认静音，除非用户手动取消
                audioElement.muted = isMuted;
                return () => {
                    audioTrack.detach(audioElement);
                };
            }
        }, [audioTrack, isMuted]);

        return <audio ref={audioRef} autoPlay playsInline />;
    });

    // 渲染参与者
    const renderParticipant = useCallback((participant: Participant, isLocal: boolean) => {
        const effectiveState = getEffectiveMediaState(participant.identity);

        return (
            <Box
                key={participant.identity}
                sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                }}
            >
                {/* 视频元素 */}
                <ParticipantVideo
                    participant={participant}
                    isLocal={isLocal}
                    isVideoEnabled={effectiveState.isVideoEnabled}
                />

                {/* 音频元素 */}
                {!isLocal && (
                    <ParticipantAudio
                        participant={participant}
                        isMuted={!effectiveState.isAudioEnabled}
                    />
                )}

                {/* 控制栏 */}
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 1,
                        bgcolor: 'rgba(0, 0, 0, 0.6)',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                    }}
                >
                    <IconButton
                        size="small"
                        color={effectiveState.isAudioEnabled ? 'primary' : 'error'}
                        onClick={() => handleToggleAudio(participant)}
                    >
                        {effectiveState.isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
                    </IconButton>
                    <IconButton
                        size="small"
                        color={effectiveState.isVideoEnabled ? 'primary' : 'error'}
                        onClick={() => handleToggleVideo(participant)}
                    >
                        {effectiveState.isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
                    </IconButton>
                    <Typography variant="caption" color="white">
                        {participant.identity} {isLocal && '(你)'}
                    </Typography>
                    )

                    {/* 本地麦克风控制 */}
                    {isLocal && (
                        <IconButton
                            size="small"
                            onClick={handleLocalMuteToggle}
                            color={isMuted ? 'error' : 'primary'}
                            sx={{ color: 'white' }}
                            title={isMuted ? '取消静音' : '静音'}
                        >
                            {isMuted ? <MicOffIcon /> : <MicIcon />}
                        </IconButton>
                    )}

                    {/* 本地视频控制 */}
                    {isLocal && (
                        <IconButton
                            size="small"
                            onClick={handleLocalVideoToggle}
                            color={isVideoEnabled ? 'primary' : 'error'}
                            sx={{ color: 'white' }}
                            title={isVideoEnabled ? '关闭摄像头' : '开启摄像头'}
                        >
                            {isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
                        </IconButton>
                    )}
                </Box>
            </Box>
        );
    }, [isMuted, isVideoEnabled, getEffectiveMediaState, handleToggleAudio, handleToggleVideo, handleLocalMuteToggle, handleLocalVideoToggle]);

    // 渲染加载状态
    if (isConnecting) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                p: 3
            }}>
                <CircularProgress />
                <Typography variant="body1" sx={{ mt: 2 }}>正在连接会议...</Typography>
            </Box>
        );
    }

    // 渲染错误状态
    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <Typography variant="body1">{error}</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={connectToRoom}
                    startIcon={<RefreshIcon />}
                    sx={{ mt: 1 }}
                >
                    重试连接
                </Button>
            </Alert>
        );
    }

    // 渲染主界面
    function renderMainContent() {
        // 如果已经连接，显示会议界面
        if (hasConnected && roomRef.current) {
            return (
                <Box className={className} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={participants.length === 0 ? 12 : 6}>
                                {renderParticipant(roomRef.current.localParticipant, true)}
                            </Grid>
                            {participants.map(function (participant) {
                                return (
                                    <Grid item xs={12} md={6} key={participant.identity}>
                                        {renderParticipant(participant, false)}
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                    {/* 底部控制栏 */}
                    <Box sx={{
                        p: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2
                    }}>
                        <Button
                            variant="contained"
                            color={!globalMediaState.isAudioEnabled ? 'error' : 'primary'}
                            onClick={toggleMute}
                            startIcon={!globalMediaState.isAudioEnabled ? <MicOffIcon /> : <MicIcon />}
                        >
                            {!globalMediaState.isAudioEnabled ? '取消静音' : '静音'}
                        </Button>

                        <Button
                            variant="contained"
                            color={!globalMediaState.isVideoEnabled ? 'error' : 'primary'}
                            onClick={toggleVideo}
                            startIcon={globalMediaState.isVideoEnabled ? <VideoIcon /> : <VideoOffIcon />}
                        >
                            {globalMediaState.isVideoEnabled ? '关闭摄像头' : '开启摄像头'}
                        </Button>

                        <Button
                            variant="contained"
                            color="error"
                            onClick={leaveMeeting}
                            startIcon={<CallEndIcon />}
                        >
                            离开会议
                        </Button>
                    </Box>
                </Box>
            );
        }

        // 未连接时显示连接界面
        return (
            <Box className={className} sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3
            }}>
                <Typography variant="h6" gutterBottom>
                    准备加入会议
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={connectToRoom}
                    disabled={isConnecting}
                    startIcon={<VideoIcon />}
                    sx={{ mt: 2 }}
                >
                    加入会议
                    {isConnecting && (
                        <CircularProgress
                            size={16}
                            sx={{
                                color: 'inherit',
                                ml: 1
                            }}
                        />
                    )}
                </Button>
            </Box>
        );
    }

    // 渲染组件
    return (
        <Box ref={ref} sx={sx} style={style}>
            {error ? (
                <Alert
                    severity="error"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={connectToRoom}
                        >
                            重试
                        </Button>
                    }
                >
                    {error}
                </Alert>
            ) : (
                renderMainContent()
            )}
        </Box>
    );
}

// 使用 forwardRef 导出组件
const MeetingRoom = forwardRef(MeetingRoomComponent);
MeetingRoom.displayName = 'MeetingRoom';

export { MeetingRoom };
